import {Chip} from "@mui/material";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ErrorBoundary} from "@variocube/app-ui";
import React, {Suspense} from "react";
import {BrowserRouter, Navigate, Outlet, Route, Routes} from "react-router-dom";
import {AuthenticationProvider, LoginHandler, LogoutHandler} from "./auth";
import {ClientIdProvider} from "./ClientIdProvider";
import {StorageLocalizationProvider} from "@smalox/shared";
import {SmaloxAppShell} from "./shell/SmaloxAppShell";
import {Stage} from "./stage";
import {TenantProvider, useTenantPermissions} from "./tenant";
import {AuditLog} from "./tenant/audit/AuditLog";
import {Bookings} from "./tenant/booking";
import {Compartments} from "./tenant/compartment";
import {Dashboard} from "./tenant/dashboard";
import {Groups} from "./tenant/group";
import {Locations} from "./tenant/location";
import {LockSearch} from "./tenant/lockscan";
import {Plans} from "./tenant/plan";
import {Settings} from "./tenant/settings";
import {Subscriptions} from "./tenant/subscription";
import {Tenants} from "./tenant/Tenants";

const Statistics = React.lazy(() => import("./tenant/statistics"));
import {Users} from "./tenant/user";
import {ThemeProvider} from "@smalox/shared";
import {UserProvider} from "./user";
import {useParam} from "./utils/useParam";

interface AppProps {
	stage: Stage;
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			staleTime: 1000,
		},
	},
});

export function App({stage}: Readonly<AppProps>) {
	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider>
				<ErrorBoundary>
					<StorageLocalizationProvider>
						<BrowserRouter>
							<ClientIdProvider>
								<AuthenticationProvider
									loginRedirectUri={`${location.origin}/auth`}
									logoutRedirectUri={`${location.origin}/logout`}
								>
									<Routes>
										<Route path="/">
											<Route path="auth" element={<LoginHandler />} />
											<Route path="logout" element={<LogoutHandler />} />
											<Route element={<AuthenticatedLayout />}>
												<Route path="tenants/*" element={<Tenants />} />
												<Route path=":tenantId" element={<Tenant />}>
													<Route path="locations/*" element={<Locations />} />
													<Route path="compartments/*" element={<Compartments />} />
													<Route path="bookings/*" element={<Bookings />} />
													<Route path="users/*" element={<Users />} />
													<Route path="groups/*" element={<Groups />} />
													<Route path="settings/*" element={<Settings />} />
													<Route path="audit/*" element={<AuditLog />} />
													<Route path="dashboard/*" element={<Dashboard />} />
													<Route path="locksearch/*" element={<LockSearch />} />
													<Route path="plans/*" element={<Plans />} />
													<Route path="subscriptions/*" element={<Subscriptions />} />
													<Route path="statistics/*" element={<Suspense fallback={null}><Statistics /></Suspense>} />
													<Route index element={<TenantIndex />} />
												</Route>
												<Route index element={<Navigate to="tenants" replace />} />
											</Route>
										</Route>
									</Routes>
								</AuthenticationProvider>
							</ClientIdProvider>
						</BrowserRouter>
					</StorageLocalizationProvider>
				</ErrorBoundary>
				<StageIndicator stage={stage} />
			</ThemeProvider>
		</QueryClientProvider>
	);
}

function AuthenticatedLayout() {
	return (
		<UserProvider>
			<Outlet />
		</UserProvider>
	);
}

export function Tenant() {
	const tenantId = useParam("tenantId");
	return (
		<TenantProvider tenantId={tenantId}>
			<SmaloxAppShell>
				<Outlet />
			</SmaloxAppShell>
		</TenantProvider>
	);
}

export function TenantIndex() {
	const dashboard = useTenantPermissions("BOOKING_READ", "TENANT_USER_READ");
	return <Navigate to={dashboard ? "dashboard" : "bookings"} />;
}

export function StageIndicator({stage}: Pick<AppProps, "stage">) {
	return stage == "production"
		? null
		: (
			<Chip
				sx={{position: "fixed", top: -4, left: "50%", transform: "translateX(-50%)"}}
				color="warning"
				size="small"
				label={stage}
			/>
		);
}
